import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card, CardHeader, CardBody, Button, StatusBadge, Loading, ErrorNotice,
  Modal, Field, Input, Table, THead, TBody, TR, TH, TD, EmptyState,
} from '../../components/ui/index.js';
import { useResource, useMutation, newIdempotencyKey } from '../../hooks/useApi.js';
import { useToast } from '../../components/ToastProvider.jsx';
import { http } from '../../lib/api.js';
import {
  formatMoney, formatTimestamp, formatExactTimestamp, toMinorUnits, fromMinorUnits,
} from '../../lib/format.js';
import { PageHero } from '../../components/PageHero.jsx';
import { GatewayDataCard } from '../../components/GatewayDataCard.jsx';
import { useAuth } from '../../lib/auth.jsx';
import { can } from '../../lib/roles.js';

const TIMELINE_LABELS = {
  'payment.created': 'Payment created',
  'checkout.opened': 'Checkout opened',
  'payment.submitted': 'Submitted to the payment network',
  'payment.approved': 'Approved',
  'payment.failed': 'Failed',
  'payment.review': 'Held for review',
  'payment.timeout': 'Outcome unconfirmed',
  'payment.cancelled': 'Cancelled',
  'refund.requested': 'Refund requested',
  'refund.succeeded': 'Refunded',
  'refund.failed': 'Refund failed',
  'provider.webhook': 'Update from the payment network',
};

const TIMELINE_TONES = {
  'payment.approved': 'bg-emerald-500',
  'refund.succeeded': 'bg-amber-500',
  'payment.failed': 'bg-red-500',
  'refund.failed': 'bg-red-500',
  'payment.timeout': 'bg-amber-500',
  'payment.review': 'bg-amber-500',
};

/**
 * RF-3: "The dashboard generates one [idempotency key] per refund dialog." One
 * per dialog, not one per click — that is what makes a double-clicked confirm
 * button safe rather than merely unlikely to be pressed twice.
 */
/**
 * Marking an offline payment received.
 *
 * This is the only action in the product that moves money to `succeeded` on a
 * human's word alone — no gateway saw anything. So it asks for the bank
 * reference, and it says plainly what it is about to do: a webhook fires, and
 * the merchant's own systems will treat this order as paid.
 */
function ConfirmOfflineDialog({ open, onClose, payment, onConfirmed }) {
  const toast = useToast();
  const [reference, setReference] = useState('');

  const { mutate, loading, error } = useMutation(async () => {
    await http.post(`/dashboard/payments/${payment.id}/confirm-offline`, {
      ...(reference.trim() ? { reference: reference.trim() } : {}),
    });
    toast.success('Marked as received', { description: 'This payment now counts as paid.' });
    onConfirmed?.();
    onClose();
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Mark this payment as received"
      blurb="Only do this once you have seen the money."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button loading={loading} onClick={() => mutate().catch(() => {})}>
            Mark as received
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); mutate().catch(() => {}); }}>
        {error && <ErrorNotice error={error} title="Could not confirm this payment" />}

        <Field
          label="Reference"
          hint="The bank reference, UTR or receipt number, so this can be reconciled later. Optional."
        >
          {(props) => (
            <Input
              {...props}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. UTR 4471928361"
              maxLength={120}
              autoComplete="off"
            />
          )}
        </Field>

        <p className="text-xs leading-relaxed text-muted">
          {formatMoney(payment.amount, payment.currency)} will be recorded as paid, and a
          <code className="mx-1 rounded bg-surface-2 px-1 py-0.5 font-mono text-[11px]">payment.succeeded</code>
          webhook will be sent. This cannot be undone — a mistake has to be corrected with a refund.
        </p>
      </form>
    </Modal>
  );
}

function RefundDialog({ open, onClose, payment, onRefunded }) {
  const toast = useToast();
  const refundable = payment.amountRefundable ?? payment.amount - payment.amountRefunded;
  const [amount, setAmount] = useState(() => fromMinorUnits(refundable, payment.currency));
  const [reason, setReason] = useState('');
  const [amountError, setAmountError] = useState(null);
  const [idempotencyKey] = useState(() => newIdempotencyKey('refund'));

  const { mutate, loading, error } = useMutation(async () => {
    const minor = toMinorUnits(amount, payment.currency);
    if (minor == null || minor <= 0) {
      setAmountError('Enter an amount greater than zero.');
      return;
    }
    if (minor > refundable) {
      setAmountError(`Only ${formatMoney(refundable, payment.currency)} remains refundable.`);
      return;
    }
    setAmountError(null);

    const result = await http.post(
      `/dashboard/payments/${payment.id}/refunds`,
      { amount: minor, ...(reason ? { reason } : {}) },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    );

    // RF-4/RF-5: the service sometimes has something specific to say — a void
    // fallback, or an unconfirmed outcome. Surface it rather than "Refunded".
    toast.success(result.message ?? 'Refund processed', {
      duration: result.message && result.message.length > 40 ? 9000 : undefined,
    });
    onRefunded?.();
    onClose();
  });

  const isFullRefund = toMinorUnits(amount, payment.currency) === refundable;

  return (
    <Modal
      open={open}
      onClose={loading ? () => {} : onClose}
      title="Refund this payment"
      // FE-10: states the consequence in plain words.
      blurb={`The money goes back to the customer's card. This cannot be undone.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="danger" onClick={() => mutate().catch(() => {})} loading={loading}>
            Refund {amount && `${formatMoney(toMinorUnits(amount, payment.currency) ?? 0, payment.currency)}`}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorNotice error={error} title="Could not refund" />}

        <Field
          label="Amount to refund"
          hint={`Up to ${formatMoney(refundable, payment.currency)} remains refundable.`}
          error={amountError}
          required
        >
          {(props) => (
            <Input {...props} inputMode="decimal" value={amount}
              invalid={Boolean(amountError)} onChange={(e) => setAmount(e.target.value)} autoFocus />
          )}
        </Field>

        <Field label="Reason" hint="Internal only — the customer never sees this.">
          {(props) => <Input {...props} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Returned item" />}
        </Field>

        <p className="rounded-md bg-surface-2 p-3 text-xs leading-relaxed text-ink-soft">
          {isFullRefund
            ? 'This refunds the payment in full. If it has not settled yet, it will be voided instead — the customer sees the charge disappear rather than a separate refund.'
            : 'This is a partial refund. If the payment has not settled yet, a partial refund may not be possible and we will tell you so rather than refunding a different amount.'}
        </p>
      </div>
    </Modal>
  );
}

export function PaymentDetailPage() {
  const { id } = useParams();
  const [refundOpen, setRefundOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { user } = useAuth();
  const { data: payment, error, loading, refetch } = useResource(`/dashboard/payments/${id}`);

  if (loading) return <Loading label="Loading payment…" />;
  if (error) {
    return (
      <>
        <PageHero title="Payment" backTo="/payments" />
        <ErrorNotice error={error} onRetry={refetch} title="Could not load this payment" />
      </>
    );
  }

  const refundable = payment.amountRefundable ?? 0;
  const canRefund = ['succeeded', 'partially_refunded'].includes(payment.status) && refundable > 0;

  // Nobody is watching an offline payment for us, so the merchant is the only
  // thing that can settle it.
  const isOffline = payment.methodKind === 'offline';
  const awaitingConfirmation = isOffline && ['processing', 'pending'].includes(payment.status);

  return (
    <>
      <PageHero
        backTo="/payments"
        title={formatMoney(payment.amount, payment.currency)}
        blurb={payment.description || 'No description'}
        actions={
          <>
            {payment.source === 'pos' && <span className="chip-neutral">Counter</span>}
            <StatusBadge status={payment.status} />
            {awaitingConfirmation && (
              <Button onClick={() => setConfirmOpen(true)}>Mark as received</Button>
            )}
            {canRefund && <Button variant="danger" onClick={() => setRefundOpen(true)}>Refund</Button>}
          </>
        }
      />

      {payment.failure && (
        <ErrorNotice
          error={{ message: payment.failure.message, code: payment.failure.code }}
          title="This payment failed"
          className="mb-6"
        />
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader title="Details" />
            <CardBody>
              <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
                {[
                  ['Payment ID', <span className="font-mono text-xs">{payment.id}</span>],
                  ['Order ID', payment.orderId ?? '—'],
                  ['Amount', formatMoney(payment.amount, payment.currency)],
                  ['Refunded', payment.amountRefunded > 0 ? formatMoney(payment.amountRefunded, payment.currency) : '—'],
                  ['Customer', payment.customer?.email ?? '—'],
                  ['Method', isOffline
                    ? <span className="capitalize">{(payment.offlineMethod ?? '').replace(/_/g, ' ')}</span>
                    : 'Card'],
                  // A row that can only ever say "—" is not worth the space:
                  // there is no card and no gateway behind an offline payment,
                  // and a reference only exists once one has been confirmed.
                  !isOffline && ['Card', payment.card
                    ? <span className="capitalize">{payment.card.brand} ···· {payment.card.last4}</span>
                    : '—'],
                  !isOffline && ['Provider transaction', <span className="font-mono text-xs">{payment.providerTransactionId ?? '—'}</span>],
                  payment.offlineReference && ['Reference', <span className="font-mono text-xs">{payment.offlineReference}</span>],
                  ['Environment', <StatusBadge status={payment.environment} size="sm" />],
                  ['Created', formatExactTimestamp(payment.createdAt)],
                  ['Succeeded', payment.succeededAt ? formatExactTimestamp(payment.succeededAt) : '—'],
                ].filter(Boolean).map(([term, value]) => (
                  <div key={term}>
                    <dt className="text-xs font-medium uppercase tracking-wide text-muted">{term}</dt>
                    <dd className="mt-1 text-sm text-ink">{value}</dd>
                  </div>
                ))}
              </dl>

              {/*
                * What the counter recorded. Kept apart from metadata below,
                * which belongs to the merchant's integration — these two were
                * typed by a person, and read by one.
                */}
              {(payment.note || Object.keys(payment.customFields ?? {}).length > 0) && (
                <div className="mt-6 border-t border-line pt-4">
                  <p className="eyebrow mb-2.5">Sale details</p>

                  {Object.keys(payment.customFields ?? {}).length > 0 && (
                    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                      {Object.entries(payment.customFields).map(([key, value]) => (
                        <div key={key}>
                          <dt className="text-xs font-medium uppercase tracking-wide text-muted">
                            {key.replace(/_/g, ' ')}
                          </dt>
                          <dd className="mt-0.5 text-sm text-ink">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  )}

                  {payment.note && (
                    <p className="mt-3 whitespace-pre-line rounded-lg border border-line bg-surface-2 px-3.5 py-2.5 text-sm leading-relaxed text-ink-soft">
                      {payment.note}
                    </p>
                  )}
                </div>
              )}

              {Object.keys(payment.metadata ?? {}).length > 0 && (
                <div className="mt-6 border-t border-line pt-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted">Metadata</p>
                  <dl className="mt-2 grid gap-2 sm:grid-cols-2">
                    {Object.entries(payment.metadata).map(([key, value]) => (
                      <div key={key} className="flex gap-2 text-xs">
                        <dt className="font-medium text-ink-soft">{key}</dt>
                        <dd className="truncate text-ink">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
            </CardBody>
          </Card>

          {/* PL-5: each charge attempt is recorded separately. */}
          <Card>
            <CardHeader title="Attempts" blurb="Each time this payment was submitted to the network" />
            {payment.attempts?.length ? (
              <Table>
                <THead><TR><TH>#</TH><TH>Outcome</TH><TH>Reason</TH><TH align="right">Took</TH><TH align="right">When</TH></TR></THead>
                <TBody>
                  {payment.attempts.map((attempt) => (
                    <TR key={attempt.id}>
                      <TD className="tabular text-xs">{attempt.attemptNumber}</TD>
                      <TD><StatusBadge status={attempt.outcome === 'approved' ? 'succeeded' : attempt.outcome === 'review' ? 'processing' : 'failed'} label={attempt.outcome} size="sm" /></TD>
                      <TD className="max-w-xs truncate text-xs text-ink-soft">{attempt.failure?.message ?? '—'}</TD>
                      <TD align="right" className="tabular text-xs text-muted">{attempt.durationMs ? `${attempt.durationMs}ms` : '—'}</TD>
                      <TD align="right" className="text-xs text-muted">{formatTimestamp(attempt.createdAt)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            ) : (
              <EmptyState title="No attempts yet" blurb="The customer has not submitted a payment on this checkout." />
            )}
          </Card>

          {/* The server refuses this to staff and viewers; hiding it here only spares them the error. */}
          {!isOffline && payment.provider && can(user?.role, 'VIEW_ACTIVITY') && (
            // Keyed so a refund made on this screen reloads it with the refund's request.
            <GatewayDataCard
              key={`${payment.attempts?.length ?? 0}-${payment.refunds?.length ?? 0}`}
              path={`/dashboard/payments/${payment.id}/gateway-data`}
            />
          )}

          {payment.refunds?.length > 0 && (
            <Card>
              <CardHeader title="Refunds" />
              <Table>
                <THead><TR><TH>Refund</TH><TH align="right">Amount</TH><TH>Status</TH><TH>Method</TH><TH align="right">When</TH></TR></THead>
                <TBody>
                  {payment.refunds.map((refund) => (
                    <TR key={refund.id}>
                      <TD className="font-mono text-xs">{refund.id}</TD>
                      <TD align="right" className="tabular font-medium">{formatMoney(refund.amount, refund.currency)}</TD>
                      <TD><StatusBadge status={refund.status} size="sm" /></TD>
                      <TD className="text-xs capitalize text-ink-soft">{refund.method}</TD>
                      <TD align="right" className="text-xs text-muted">{formatTimestamp(refund.createdAt)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </Card>
          )}
        </div>

        {/* PL-4: the timeline, rendered verbatim. */}
        <Card className="h-fit">
          <CardHeader title="Timeline" />
          <CardBody>
            <ol className="relative space-y-5 pl-5">
              <span className="absolute left-[3px] top-2 bottom-2 w-px bg-line" aria-hidden="true" />
              {payment.timeline?.map((entry, index) => (
                <li key={`${entry.type}-${index}`} className="relative">
                  <span
                    className={`absolute -left-5 top-1.5 h-[7px] w-[7px] rounded-full ring-2 ring-surface ${TIMELINE_TONES[entry.type] ?? 'bg-slate-300'}`}
                    aria-hidden="true"
                  />
                  <p className="text-sm font-medium text-ink">{TIMELINE_LABELS[entry.type] ?? entry.type}</p>
                  {entry.message && entry.message !== TIMELINE_LABELS[entry.type] && (
                    <p className="mt-0.5 text-xs leading-relaxed text-ink-soft">{entry.message}</p>
                  )}
                  <time className="mt-0.5 block text-[11px] text-faint" dateTime={entry.at}>
                    {formatExactTimestamp(entry.at)}
                  </time>
                </li>
              ))}
            </ol>
          </CardBody>
        </Card>
      </div>

      <ConfirmOfflineDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        payment={payment}
        onConfirmed={refetch}
      />

      {refundOpen && (
        <RefundDialog
          open={refundOpen}
          onClose={() => setRefundOpen(false)}
          payment={payment}
          onRefunded={refetch}
        />
      )}
    </>
  );
}

export default PaymentDetailPage;
